import React, { useId, useRef, useState } from "react";
import { ExternalLink, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { SOURCE_TYPES, SourceTypeMark, SourceTypeLegend, sourceGroups } from "./SourceTypeMark";

const countryLabel = (slug) => (slug || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const sourcesLabel = (story) => {
  const n = story?.source_count || 0;
  const c = story?.country_count || 0;
  if (n >= 2) return `${n} publishers${c >= 2 ? ` · ${c} countries` : ""}`;
  if (n === 1 && story?.source_name) {
    const name = story.source_name.length > 26 ? `${story.source_name.slice(0, 24)}…` : story.source_name;
    return `via ${name}`;
  }
  return "Sources";
};

export const StorySources = ({ story, showLabel = true, light = false, publisherLabel = false }) => {
  const uid = useId().replace(/:/g, "");
  const trigger = useRef(null);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const show = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
    setError(false);
    setData(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/story/${story.id}/sources`, { cache: "no-store" });
      if (!response.ok) throw new Error("Sources unavailable");
      setData(await response.json());
    } catch { setError(true); }
  };
  const sources = data?.sources || [];
  const groups = sourceGroups(data || story);
  const kind = groups.length > 1 ? "Mixed sources" : SOURCE_TYPES[groups[0]]?.label || "Sources";
  const title = `${kind}${groups.length > 1 ? `: ${groups.map(g => SOURCE_TYPES[g].short).join(", ")}` : ""} — View sources`;
  return <span className="inline-flex min-w-0" data-card-interactive onClick={(event) => event.stopPropagation()}>
    <button ref={trigger} type="button" onClick={show} data-testid={`story-sources-${story.id}-${uid}`}
      aria-haspopup="dialog" aria-expanded={open}
      aria-label={title} title={title}
      className={`inline-flex items-center gap-2 min-h-11 min-w-11 max-w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--crimson)] rounded-md transition-colors ${light ? "text-white" : "text-[var(--muted)] hover:text-[var(--crimson)]"}`}>
      <SourceTypeMark groups={groups} testId={`source-type-icon-${story.id}-${uid}`} />
      {showLabel && <span className="text-[10px] truncate min-w-0" data-testid={`source-type-label-${story.id}-${uid}`}>{publisherLabel ? <><span className="block truncate text-[var(--ink-2)]">{story.source_name || "Source evidence"}</span><span className="block truncate text-[9px]">{groups.length > 1 ? "Mixed sources" : (data?.source_count || story.source_count) > 1 ? `${data?.source_count || story.source_count} linked publishers` : SOURCE_TYPES[groups[0]]?.short || "Sources"}</span></> : groups.length > 1 ? "Mixed sources" : sourcesLabel({ ...story, ...data })}</span>}
    </button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid={`story-sources-dialog-${uid}`} onCloseAutoFocus={event => { event.preventDefault(); trigger.current?.focus(); }} className="max-w-[calc(100vw-2rem)] sm:max-w-lg bg-[var(--paper)] text-[var(--ink)] [&>button:last-child]:hidden">
        <DialogTitle className="pr-8" data-testid={`story-sources-title-${uid}`}>Sources behind this story</DialogTitle>
        <DialogDescription className="break-words" data-testid={`story-sources-description-${uid}`}>{story.headline}</DialogDescription>
        <button type="button" aria-label="Close sources" data-testid={`story-sources-close-${uid}`} onClick={() => setOpen(false)} className="absolute right-2 top-2 w-11 h-11 grid place-items-center"><X size={16} /></button>
        {data && (
          <p data-testid={`story-sources-summary-${uid}`} className="font-mono text-[10.5px] tracking-[0.08em] uppercase text-[var(--muted)]">
            {data.source_count} {data.source_count === 1 ? "publisher" : "publishers"}
            {data.country_count > 0 && ` · ${data.country_count} ${data.country_count === 1 ? "country" : "countries"}`}
          </p>
        )}
        <div className="max-h-[55vh] overflow-y-auto" data-testid={`story-sources-content-${uid}`}>
          {groups.length > 0 && <SourceTypeLegend groups={groups} prefix={`source-evidence-${uid}`} />}
          {sources?.some(s => ["government", "institution", "authority"].includes(s.source_group)) && <p className="text-xs text-[var(--muted)] py-2" data-testid={`story-official-position-note-${uid}`}>Official statements represent the issuing organization’s position, not independent confirmation of every claim.</p>}
          {error ? <p role="alert" data-testid={`story-sources-error-${uid}`}>Sources are temporarily unavailable.</p>
            : data === null ? <p data-testid={`story-sources-loading-${uid}`}>Loading sources…</p>
            : sources.length === 0 ? <p data-testid={`story-sources-empty-${uid}`}>No linked source evidence is available.</p>
            : sources.map((source, index) => <a key={source.url} href={source.url} target="_blank" rel="noopener noreferrer"
                data-testid={`story-source-link-${uid}-${index}`} className="flex items-center justify-between gap-3 py-3 border-b border-[var(--hairline)] hover:text-[var(--crimson)] transition-colors">
                <span className="min-w-0 flex flex-col gap-1">
                  <span className="break-words" data-testid={`story-source-name-${uid}-${index}`}>{source.name}</span>
                  <span data-testid={`story-source-kind-${uid}-${index}`} className="text-xs text-[var(--muted)]">{SOURCE_TYPES[source.source_group]?.label || "Source type not recorded"}</span>
                  {source.country && <span className="font-mono text-[10px] text-[var(--muted)]" data-testid={`story-source-country-${uid}-${index}`}>{countryLabel(source.country)}</span>}
                </span>
                <ExternalLink size={14} className="shrink-0" />
              </a>)}
        </div>
      </DialogContent>
    </Dialog>
  </span>;
};
